import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Smart Food Choices for Daily Life',
    content: [
      '<strong>Lesson Focus:</strong> Making Healthy Eating Simple and Affordable'
    ],
    additionalSection: {
      title: 'Deep Dive:',
      content: [
        'Healthy eating is often undermined by lack of planning and budgeting. Affordable health comes from relying on local, whole ingredients (seasonal fruits, monggo, cheap fish) instead of costly, processed convenience foods. By implementing simple Practical Tips, you gain control over your food environment.',
      ]
    },
    additionalSection2: {
      title: 'The Power of Simple Habits:',
      content: [
        '• <strong>Plan Ahead:</strong> Plan tomorrow\'s meals tonight to save time and money.',
        '• <strong>Bring Snacks:</strong> Packing a piece of fruit or a handful of nuts keeps blood sugar stable.',
        '• <strong>Don\'t Skip Breakfast:</strong> A balanced breakfast boosts metabolism and prevents extreme hunger that leads to overeating later.',
      ]
    }
  },
  {
    title: 'Activity: Budget Meal Plan',
    content: [
      '• <strong>Action:</strong> Plan your meals for one day using budget-friendly Filipino ingredients (e.g., tuyo + rice + banana; monggo + rice + ampalaya).'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      "You've completed the module content! Now, let's see how well you understand mindful eating and portion control."
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your understanding of mindful eating practices and portion control techniques.',
        'The quiz will help you review key concepts about hunger signals, fullness cues, and practical mindfulness strategies.'
      ]
    }
  }
];

const NutritionalModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM4');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/nutritional-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Smart Food Choices for Daily Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Discover practical strategies for making healthy food choices in everyday situations, from grocery shopping to eating out.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule4Page;

